import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

/**
 * Modal question shown when importing a database config (single) or a bundle
 * (bulk). Replaces the old persistent "Rebuild Fast Search" checkbox: the
 * choice is now made per-import, at the moment of action.
 *
 * The user picks one of three actions:
 *   Cancel                       — abort the import entirely
 *   Import without rebuilding    — onChoose(false): bring in index definitions only
 *   Import & Rebuild Fast Search — onChoose(true): also queue physical DB-index builds
 *
 * Props:
 *   open      — when falsy, renders nothing
 *   kind      — "single" | "bundle" (affects nothing visual today; kept for callers)
 *   onChoose  — (rebuild: boolean) => void
 *   onCancel  — () => void
 */
export default function FastSearchImportModal({ open, onChoose, onCancel }) {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  if (!open) return null;

  return (
    <div className="dbModalOverlay" onClick={onCancel}>
      <div className="dbModalCard dbModalConfirmCard" onClick={(e) => e.stopPropagation()}>
        <button
          className="drawer-close-btn drawer-close-btn-abs"
          onClick={onCancel}
          aria-label="Close"
          title="Close"
        >
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none" aria-hidden="true">
            <line x1="1" y1="1" x2="7" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            <line x1="7" y1="1" x2="1" y2="7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>

        <div className="dbModalConfirmBadge dbModalConfirmBadge--info">#</div>

        <div className="dbModalTitle">
          {t("admin.fastSearchImportTitle", { defaultValue: "Rebuild Fast Search indexes?" })}
        </div>

        <div className="dbModalConfirmBanner dbModalConfirmBanner--info">
          <p>
            {t("admin.rebuildFastSearchHint", {
              defaultValue: "When importing, also rebuild the Fast Search (physical database) indexes the source had. Off by default — imports never build indexes unless you tick this.",
            })}
          </p>
        </div>

        <div className="dbModalConfirmActions">
          <button
            type="button"
            className="dbModalConfirmBtn dbModalConfirmBtn--warn"
            onClick={() => onChoose(false)}
          >
            {t("admin.fastSearchImportSkip", { defaultValue: "Import without rebuilding" })}
          </button>
          <button
            type="button"
            className="dbModalConfirmBtn dbModalConfirmBtn--info"
            onClick={() => onChoose(true)}
          >
            {t("admin.fastSearchImportRebuild", { defaultValue: "Import & Rebuild Fast Search" })}
          </button>
        </div>
      </div>
    </div>
  );
}
